import { useState, useEffect, useCallback } from 'react';
import { Form, Button } from 'react-bootstrap';
import Accordion from 'react-bootstrap/Accordion';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaXTwitter } from 'react-icons/fa6';

// "Upload to X" panel on the /upload page (hide with REACT_APP_ENABLE_X=false).
// Backend contract (socialuploader-backend):
//   GET  /auth/x/url?customParam=<callback>  -> { authUrl }
//        OAuth returns to /calllback?x_token=<jwt>, which posts { type: 'xSuccess', x_token } back here.
//   GET  /auth/x/userinfo   (Bearer x_token)  -> { data: { id, username, name, profile_image_url } }
//   POST /upload/x          (Bearer x_token)  { video_url, text } -> { postUrl }

const MAX_X_TEXT = 280;
const URL_PATTERN = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;

// X charges much more for posts with links, so links are removed before posting.
export const stripLinks = (text) => text.replace(URL_PATTERN, '').replace(/[ \t]{2,}/g, ' ').trim();

const XUploadPanel = ({ eventKey, apiUrl, callbackUrl, videoUrl }) => {
    const [xToken, setXToken] = useState(() => localStorage.getItem('xToken'));
    const [account, setAccount] = useState(null);
    const [text, setText] = useState('');
    const [agreed, setAgreed] = useState(false);
    const [isPosting, setIsPosting] = useState(false);
    const [postUrl, setPostUrl] = useState('');

    const disconnect = useCallback(() => {
        localStorage.removeItem('xToken');
        setXToken(null);
        setAccount(null);
        setPostUrl('');
    }, []);

    // Receive the token from the OAuth popup (AuthProcessing).
    useEffect(() => {
        const onMessage = (event) => {
            if (event.origin !== window.location.origin) return;
            if (event.data?.type === 'xSuccess' && event.data.x_token) {
                localStorage.setItem('xToken', event.data.x_token);
                setXToken(event.data.x_token);
            }
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, []);

    // Load the connected account; a rejected token means the user must reconnect.
    useEffect(() => {
        if (!xToken) return;
        axios.get(`${apiUrl}/auth/x/userinfo`, { headers: { Authorization: `Bearer ${xToken}` } })
            .then((res) => setAccount(res.data?.data || null))
            .catch((error) => {
                console.error('Error fetching X user info:', error);
                if (error.response?.status === 401) disconnect();
            });
    }, [xToken, apiUrl, disconnect]);

    const connect = () => {
        axios.get(`${apiUrl}/auth/x/url?customParam=${encodeURIComponent(callbackUrl)}`)
            .then((res) => {
                window.open(res.data.authUrl, 'targetWindow',
                    'toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=600,height=700');
            })
            .catch((error) => {
                console.error('Failed to get X login URL:', error);
                toast.error('Could not start X sign-in. Please try again.');
            });
    };

    const cleanText = stripLinks(text);
    const hasLinks = cleanText !== text.trim();
    const remaining = MAX_X_TEXT - cleanText.length;

    const submit = (event) => {
        event.preventDefault();
        if (!cleanText) {
            toast.error('Please write a caption for your post.');
            return;
        }
        if (remaining < 0) {
            toast.error(`Captions on X can be up to ${MAX_X_TEXT} characters.`);
            return;
        }
        if (!agreed) {
            toast.error("Please agree to X's Terms of Service and the X Rules.");
            return;
        }
        setIsPosting(true);
        setPostUrl('');
        axios.post(`${apiUrl}/upload/x`, { video_url: videoUrl, text: cleanText }, {
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${xToken}` },
        })
            .then((res) => {
                setPostUrl(res.data?.postUrl || '');
                toast.success('Your video was posted to X.');
            })
            .catch((error) => {
                if (error.response?.status === 401) {
                    disconnect();
                    toast.error('Your X connection expired. Please connect again.');
                    return;
                }
                const apiError = error.response?.data?.error || error.response?.data?.message;
                toast.error(typeof apiError === 'string' ? apiError : 'Posting to X failed. Please try again.');
            })
            .finally(() => setIsPosting(false));
    };

    return (
        <Accordion.Item eventKey={eventKey}>
            <Accordion.Header>
                <FaXTwitter className='socialIconsSmalll' style={{ width: 22, height: 22 }} aria-hidden='true' />
                Upload to X{' '}
                {xToken && (
                    // A span, not a button: the accordion header is already a <button>.
                    <span
                        role='button'
                        tabIndex={0}
                        className='disconnectbtn btn btn-lg btn-link'
                        onClick={(e) => { e.stopPropagation(); disconnect(); }}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); disconnect(); } }}
                    >
                        Disconnect
                    </span>
                )}
            </Accordion.Header>
            <Accordion.Body>
                {!xToken && (
                    <button type='button' className='btn btn-dark d-inline-flex align-items-center gap-2' onClick={connect}>
                        <FaXTwitter aria-hidden='true' /> Connect with X
                    </button>
                )}

                {xToken && (
                    <div className='row'>
                        <div className='col-md-4'>
                            <div className='shadow videoPreview'>
                                <video controls='controls'>
                                    <source src={videoUrl} type='video/mp4' />
                                </video>
                            </div>
                        </div>
                        <div className='col-md-8'>
                            <form onSubmit={submit}>
                                {account?.username && (
                                    <div className='d-flex align-items-center gap-2 mb-3'>
                                        {account.profile_image_url && (
                                            <img src={account.profile_image_url} alt='' className='rounded-circle' style={{ width: 32, height: 32 }} />
                                        )}
                                        <span>Posting as <strong>@{account.username}</strong></span>
                                    </div>
                                )}

                                <Form.Group className='mb-2' controlId='xForm.text'>
                                    <Form.Label>Caption</Form.Label>
                                    <Form.Control
                                        as='textarea'
                                        rows={4}
                                        placeholder='Write a caption and add hashtags'
                                        value={text}
                                        onChange={(e) => setText(e.target.value)}
                                    />
                                    <div className='d-flex justify-content-between mt-1' style={{ fontSize: 13 }}>
                                        <span className='text-muted'>{hasLinks ? 'Links are removed on X.' : ''}</span>
                                        <span className={remaining < 0 ? 'text-danger' : 'text-muted'}>{remaining}</span>
                                    </div>
                                </Form.Group>

                                <Form.Check
                                    className='mb-3'
                                    type='checkbox'
                                    id='xForm.agree'
                                    checked={agreed}
                                    onChange={(e) => setAgreed(e.target.checked)}
                                    label={<>I agree to <a href='https://x.com/en/tos' target='_blank' rel='noopener noreferrer'>X's Terms of Service</a> and <a href='https://help.x.com/en/rules-and-policies/x-rules' target='_blank' rel='noopener noreferrer'>the X Rules</a>, and I have the rights to this video.</>}
                                />

                                <Button variant='dark' className='btn-full' type='submit' disabled={isPosting}>
                                    {isPosting ? 'Posting to X…' : 'Post to X'}
                                </Button>

                                {postUrl && (
                                    <p className='mt-3'>
                                        <a className='btn btn-outline-success' href={postUrl} target='_blank' rel='noopener noreferrer'>View post on X</a>
                                    </p>
                                )}
                            </form>
                        </div>
                    </div>
                )}
            </Accordion.Body>
        </Accordion.Item>
    );
};

export default XUploadPanel;
